import { Button, ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUser } from "@/hooks/useUserProvider";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

const DEFAULT_DOMAIN = "bitrix24.com";

const normalizePortalUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error("Please enter your Bitrix24 domain.");
  }

  const looksLikeOrg = /^[a-z0-9-]+$/i.test(trimmed);
  const valueWithDomain = looksLikeOrg
    ? `${trimmed}.${DEFAULT_DOMAIN}`
    : trimmed;

  const ensureProtocol = valueWithDomain.startsWith("http")
    ? valueWithDomain
    : `https://${valueWithDomain}`;

  let url: URL;
  try {
    url = new URL(ensureProtocol);
  } catch {
    throw new Error("Enter a valid Bitrix24 URL or workspace name.");
  }

  let host = url.hostname.toLowerCase();

  if (!host.includes(".")) {
    host = `${host}.${DEFAULT_DOMAIN}`;
  }

  if (host.endsWith(".bitrix24")) {
    host = `${host}.com`;
  }

  const parts = host.split(".");
  if (parts.length < 3 || parts[0] === "www" || parts[0] === "bitrix24") {
    throw new Error("Include your workspace name, e.g. acme.bitrix24.com.");
  }

  return `https://${host}`;
};

const bitrix24ConnectSchema = z.object({
  domain: z.string().url("Enter a valid Bitrix24 URL."),
  client_id: z.string(),
  client_secret: z.string(),
});

export const Bitrix24 = ({
  children,
  size,
  variant,
  className,
}: {
  children: React.ReactNode;
  size: ButtonProps["size"];
  variant: ButtonProps["variant"];
  className?: string;
}) => {
  const { accessToken } = useUser();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<z.infer<typeof bitrix24ConnectSchema>>({
    resolver: zodResolver(bitrix24ConnectSchema),
    defaultValues: {
      domain: "",
      client_id: "",
      client_secret: "",
    },
  });

  const domainWatch = form.watch("domain");

  useEffect(() => {
    if (!open) {
      form.reset();
      setError(null);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.reset]);

  const helperExample = useMemo(() => {
    if (!domainWatch.trim()) {
      return "https://acme.bitrix24.com";
    }

    try {
      return normalizePortalUrl(domainWatch);
    } catch {
      return "https://acme.bitrix24.com";
    }
  }, [domainWatch]);

  const handleSubmit = async (
    values: z.infer<typeof bitrix24ConnectSchema>,
  ) => {
    try {
      setError(null);
      setSubmitting(true);
      captureEvent("onboarding_connect_started", {
        service: "bitrix24",
        flow: "oauth",
      });

      const query = new URLSearchParams({
        portal: normalizePortalUrl(values.domain),
        client_id: values.client_id,
        client_secret: values.client_secret,
      });
      if (accessToken) {
        query.append("token", accessToken);
      }

      window.location.href = `${import.meta.env.VITE_BASE_URL}/auth/bitrix24/connect?${query.toString()}`;
    } catch (err) {
      setSubmitting(false);
      captureEvent("onboarding_connect_failed", {
        service: "bitrix24",
        flow: "oauth",
        reason: sanitizeErrorReason(err),
      });
      setError(err instanceof Error ? err.message : "Unable to parse domain.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Bitrix24</DialogTitle>
          <DialogDescription>
            Provide your Bitrix24 workspace domain to start the integration.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="domain"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>Workspace Domain</FormLabel>
                  <Input
                    id="bitrix24-portal"
                    placeholder="https://acme.bitrix24.com"
                    className={cn({ "border-red-500": error })}
                    {...field}
                  />
                  <FormMessage />
                  <FormDescription className="text-xs text-muted-foreground">
                    We will redirect you to Bitrix24 at {helperExample}
                  </FormDescription>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="client_id"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>Client ID</FormLabel>
                  <Input
                    id="bitrix24-client-id"
                    placeholder="Enter your Bitrix24 Client ID"
                    className={cn({ "border-red-500": error })}
                    {...field}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="client_secret"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>Client Secret</FormLabel>
                  <Input
                    id="bitrix24-client-secret"
                    placeholder="Enter your Bitrix24 Client Secret"
                    className={cn({ "border-red-500": error })}
                    {...field}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            {error ? <p className="text-sm text-red-500">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2Icon className="size-4 animate-spin" />
                    Redirecting
                  </>
                ) : (
                  <>Continue</>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
